import React, { useMemo } from "react";
import {
  Alert,
  Badge,
  Button,
  Card,
  Spinner,
  Table,
} from "flowbite-react";
import {
  HiOutlineOfficeBuilding,
  HiOutlineCheckCircle,
  HiOutlineClock,
  HiOutlineExclamation,
  HiOutlineUsers,
  HiOutlineChartBar,
  HiOutlineArrowRight,
} from "react-icons/hi";
import { useQuery } from "react-query";
import { Link } from "react-router-dom";
import moment from "moment";
import axios from "../config/axiosInstance";
import { ADMIN } from "../config/apiConfig";

export default function Dashboard() {
  const { data, isLoading, error } = useQuery(
    ["admin-dashboard", "companies-overview"],
    async () => {
      const response = await axios.get(ADMIN.GET_COMPANIES_OVERVIEW, {
        params: { itemsPerPage: -1 },
      });
      return response.data?.data || null;
    },
    { staleTime: 5 * 60 * 1000 }
  );

  const companies = data?.companies ?? [];
  const totals = data?.totals ?? {
    companies: 0,
    funnels: 0,
    teams: 0,
    users: 0,
  };

  const formatDate = (value) => {
    if (!value) return "—";
    const parsed = moment(value);
    return parsed.isValid() ? parsed.format("DD/MM/YYYY") : "—";
  };

  const formatShortDate = (value) => {
    if (!value) return "—";
    const parsed = moment(value);
    return parsed.isValid() ? parsed.format("DD/MM") : "—";
  };

  const totalsSnapshot = useMemo(() => {
    const paid = companies.filter((company) => !company.isTrial).length;
    const trial = companies.filter((company) => company.isTrial).length;
    const expired = companies.filter(
      (company) =>
        company.planExpirationDate &&
        moment().isAfter(company.planExpirationDate, "day")
    ).length;
    const expiringSoon = companies.filter((company) => {
      if (!company.planExpirationDate) return false;
      const diff = moment(company.planExpirationDate).diff(moment(), "days");
      return diff >= 0 && diff <= 14;
    }).length;

    return { paid, trial, expired, expiringSoon };
  }, [companies]);

  const newestCompanies = useMemo(() => {
    return [...companies]
      .sort(
        (a, b) =>
          new Date(b.createdAt || 0).getTime() -
          new Date(a.createdAt || 0).getTime()
      )
      .slice(0, 6);
  }, [companies]);

  const recentPaidCompanies = useMemo(() => {
    return [...companies]
      .filter((company) => !company.isTrial)
      .sort(
        (a, b) =>
          new Date(b.updatedAt || b.createdAt || 0).getTime() -
          new Date(a.updatedAt || a.createdAt || 0).getTime()
      )
      .slice(0, 6);
  }, [companies]);

  const expiringCompanies = useMemo(() => {
    return [...companies]
      .filter((company) => company.planExpirationDate)
      .sort(
        (a, b) =>
          new Date(a.planExpirationDate || 0).getTime() -
          new Date(b.planExpirationDate || 0).getTime()
      )
      .slice(0, 6);
  }, [companies]);

  const topCompaniesByUsers = useMemo(() => {
    return [...companies]
      .sort(
        (a, b) =>
          (b.counts?.users || 0) - (a.counts?.users || 0)
      )
      .slice(0, 8);
  }, [companies]);

  const planDistribution = useMemo(() => {
    const map = new Map();
    companies.forEach((company) => {
      const label =
        company.plan?.name ||
        company.plan?.code ||
        (company.isTrial ? "Teste" : "Plano não informado");
      map.set(label, (map.get(label) || 0) + 1);
    });
    return Array.from(map.entries())
      .map(([label, count]) => ({ label, count }))
      .sort((a, b) => b.count - a.count)
      .slice(0, 6);
  }, [companies]);

  const statusLabel = (company) => {
    if (company?.status === 0) return "Bloqueada";
    if (company?.status === -1) return "Inativa";
    return "Ativa";
  };

  const statusColor = (company) => {
    if (company?.status === 0) return "failure";
    if (company?.status === -1) return "gray";
    return company?.isTrial ? "warning" : "success";
  };

  const planLabel = (company) =>
    company.plan?.name ||
    company.plan?.code ||
    (company.isTrial ? "Plano teste" : "Plano não informado");

  return (
    <div className="space-y-6">
      <header className="flex flex-wrap items-center justify-between gap-4">
        <div className="space-y-1">
          <p className="text-xs uppercase tracking-[0.4em] text-slate-400">
            Backoffice
          </p>
          <h1 className="text-2xl font-semibold text-slate-900">
            Painel executivo
          </h1>
          <p className="text-sm text-slate-500">
            Visão geral das empresas, contratos e saúde operacional.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Link to="/companies">
            <Button color="gray" size="sm">
              Ver empresas
            </Button>
          </Link>
        </div>
      </header>

      <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-5">
        <div className="rounded-2xl border border-slate-200 bg-white shadow-sm p-3">
          <div className="flex items-center justify-between">
            <p className="text-[11px] uppercase tracking-[0.3em] text-slate-400">
              Empresas
            </p>
            <HiOutlineOfficeBuilding className="h-4 w-4 text-slate-400" />
          </div>
          <p className="mt-3 text-2xl font-semibold text-slate-900">
            {totals.companies}
          </p>
          <p className="mt-1 text-xs text-slate-500">
            {totalsSnapshot.trial} em teste · {totalsSnapshot.paid} contratadas
          </p>
        </div>
        <div className="rounded-2xl border border-slate-200 bg-white shadow-sm p-3">
          <div className="flex items-center justify-between">
            <p className="text-[11px] uppercase tracking-[0.3em] text-slate-400">
              Expiram em 14d
            </p>
            <HiOutlineClock className="h-4 w-4 text-slate-400" />
          </div>
          <p className="mt-3 text-2xl font-semibold text-slate-900">
            {totalsSnapshot.expiringSoon}
          </p>
          <p className="mt-1 text-xs text-slate-500">
            Acompanhar renovações
          </p>
        </div>
        <div className="rounded-2xl border border-slate-200 bg-white shadow-sm p-3">
          <div className="flex items-center justify-between">
            <p className="text-[11px] uppercase tracking-[0.3em] text-slate-400">
              Expiradas
            </p>
            <HiOutlineExclamation className="h-4 w-4 text-rose-500" />
          </div>
          <p className="mt-3 text-2xl font-semibold text-slate-900">
            {totalsSnapshot.expired}
          </p>
          <p className="mt-1 text-xs text-slate-500">
            Necessário contato imediato
          </p>
        </div>
        <div className="rounded-2xl border border-slate-200 bg-white shadow-sm p-3">
          <div className="flex items-center justify-between">
            <p className="text-[11px] uppercase tracking-[0.3em] text-slate-400">
              Funis ativos
            </p>
            <HiOutlineChartBar className="h-4 w-4 text-slate-400" />
          </div>
          <p className="mt-3 text-2xl font-semibold text-slate-900">
            {totals.funnels}
          </p>
          <p className="mt-1 text-xs text-slate-500">Total consolidado</p>
        </div>
        <div className="rounded-2xl border border-slate-200 bg-white shadow-sm p-3">
          <div className="flex items-center justify-between">
            <p className="text-[11px] uppercase tracking-[0.3em] text-slate-400">
              Usuários
            </p>
            <HiOutlineCheckCircle className="h-4 w-4 text-slate-400" />
          </div>
          <p className="mt-3 text-2xl font-semibold text-slate-900">
            {totals.users}
          </p>
          <p className="mt-1 text-xs text-slate-500">Total consolidado</p>
        </div>
      </div>

      {isLoading ? (
        <div className="flex flex-col items-center justify-center gap-2 py-12 text-sm text-gray-500">
          <Spinner color="info" />
          Carregando dados do backoffice...
        </div>
      ) : error ? (
        <Alert color="failure">
          <span className="font-semibold">
            Não foi possível carregar o dashboard.
          </span>{" "}
          Tente novamente mais tarde.
        </Alert>
      ) : (
        <>
          <div className="grid gap-4 lg:grid-cols-3">
            <div className="rounded-2xl border border-slate-200 bg-white shadow-sm p-3">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs uppercase tracking-[0.3em] text-slate-400">
                    Novas empresas
                  </p>
                  <p className="text-sm font-semibold text-slate-900">
                    Últimos cadastros
                  </p>
                </div>
                <HiOutlineOfficeBuilding className="h-5 w-5 text-slate-400" />
              </div>
              <div className="mt-4 space-y-3">
                {newestCompanies.map((company) => (
                  <div
                    key={company.id}
                    className="flex items-center justify-between rounded-xl border border-slate-100 bg-slate-50 px-3 py-2"
                  >
                    <div>
                      <p className="text-sm font-semibold text-slate-900">
                        {company.socialReason || company.name}
                      </p>
                      <p className="text-[11px] text-slate-500">
                        Criada em {formatDate(company.createdAt)}
                      </p>
                    </div>
                    <Badge color={company.isTrial ? "warning" : "success"}>
                      {company.isTrial ? "Teste" : "Contrato"}
                    </Badge>
                  </div>
                ))}
                {!newestCompanies.length && (
                  <p className="text-sm text-slate-500">
                    Nenhuma empresa recente encontrada.
                  </p>
                )}
              </div>
            </div>

            <div className="rounded-2xl border border-slate-200 bg-white shadow-sm p-3">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs uppercase tracking-[0.3em] text-slate-400">
                    Contratos recentes
                  </p>
                  <p className="text-sm font-semibold text-slate-900">
                    Últimos planos contratados
                  </p>
                </div>
                <HiOutlineCheckCircle className="h-5 w-5 text-slate-400" />
              </div>
              <div className="mt-4 space-y-3">
                {recentPaidCompanies.map((company) => {
                  const referenceDate =
                    company.updatedAt || company.createdAt || null;
                  const relativeDate = referenceDate
                    ? moment(referenceDate).fromNow()
                    : "—";
                  return (
                    <div
                      key={company.id}
                      className="flex items-center justify-between rounded-xl border border-slate-100 bg-white px-3 py-2"
                    >
                      <div>
                        <p className="text-sm font-semibold text-slate-900">
                          {company.socialReason || company.name}
                        </p>
                        <p className="text-[11px] text-slate-500">
                          {planLabel(company)} ·{" "}
                          {formatShortDate(company.updatedAt)}
                        </p>
                      </div>
                      <span className="text-xs text-slate-500">
                        {relativeDate}
                      </span>
                    </div>
                  );
                })}
                {!recentPaidCompanies.length && (
                  <p className="text-sm text-slate-500">
                    Nenhum contrato recente encontrado.
                  </p>
                )}
              </div>
            </div>

            <div className="rounded-2xl border border-slate-200 bg-white shadow-sm p-3">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs uppercase tracking-[0.3em] text-slate-400">
                    Alertas
                  </p>
                  <p className="text-sm font-semibold text-slate-900">
                    Renovação & bloqueios
                  </p>
                </div>
                <HiOutlineExclamation className="h-5 w-5 text-rose-500" />
              </div>
              <div className="mt-4 space-y-3">
                {expiringCompanies.map((company) => {
                  const isExpired =
                    company.planExpirationDate &&
                    moment().isAfter(company.planExpirationDate, "day");
                  return (
                    <div
                      key={company.id}
                      className="flex items-center justify-between rounded-xl border border-slate-100 bg-white px-3 py-2"
                    >
                      <div>
                        <p className="text-sm font-semibold text-slate-900">
                          {company.socialReason || company.name}
                        </p>
                        <p
                          className={`text-[11px] ${
                            isExpired ? "text-rose-600" : "text-slate-500"
                          }`}
                        >
                          {isExpired
                            ? "Plano expirado"
                            : "Expira em"}{" "}
                          {formatDate(company.planExpirationDate)}
                        </p>
                      </div>
                      <Badge color={isExpired ? "failure" : "warning"}>
                        {isExpired ? "Expirado" : "A vencer"}
                      </Badge>
                    </div>
                  );
                })}
                {!expiringCompanies.length && (
                  <p className="text-sm text-slate-500">
                    Nenhuma empresa com vencimento próximo.
                  </p>
                )}
              </div>
            </div>
          </div>

          <div className="grid gap-4 lg:grid-cols-2">
            <div className="rounded-2xl border border-slate-200 bg-white shadow-sm p-3">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs uppercase tracking-[0.3em] text-slate-400">
                    Top empresas
                  </p>
                  <p className="text-sm font-semibold text-slate-900">
                    Maior base de usuários
                  </p>
                </div>
                <HiOutlineUsers className="h-5 w-5 text-slate-400" />
              </div>
              <div className="mt-4">
                <Table hoverable className="min-w-[420px]">
                  <Table.Head className="bg-slate-50">
                    <Table.HeadCell className="text-[11px] uppercase tracking-[0.3em] text-slate-500">
                      Empresa
                    </Table.HeadCell>
                    <Table.HeadCell className="text-right text-[11px] uppercase tracking-[0.3em] text-slate-500">
                      Usuários
                    </Table.HeadCell>
                    <Table.HeadCell className="text-right text-[11px] uppercase tracking-[0.3em] text-slate-500">
                      Funis
                    </Table.HeadCell>
                  </Table.Head>
                  <Table.Body className="divide-y divide-slate-100">
                    {topCompaniesByUsers.map((company) => (
                      <Table.Row key={company.id} className="bg-white">
                        <Table.Cell>
                          <p className="text-sm font-semibold text-slate-900">
                            {company.socialReason || company.name}
                          </p>
                          <p className="text-[11px] text-slate-500">
                            {planLabel(company)}
                          </p>
                        </Table.Cell>
                        <Table.Cell className="text-right font-semibold text-slate-900">
                          {company.counts?.users ?? 0}
                        </Table.Cell>
                        <Table.Cell className="text-right font-semibold text-slate-900">
                          {company.counts?.funnels ?? 0}
                        </Table.Cell>
                      </Table.Row>
                    ))}
                  </Table.Body>
                </Table>
              </div>
            </div>

            <div className="rounded-2xl border border-slate-200 bg-white shadow-sm p-3">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs uppercase tracking-[0.3em] text-slate-400">
                    Planos em destaque
                  </p>
                  <p className="text-sm font-semibold text-slate-900">
                    Distribuição atual
                  </p>
                </div>
                <HiOutlineChartBar className="h-5 w-5 text-slate-400" />
              </div>
              <div className="mt-4 space-y-3">
                {planDistribution.map((plan) => (
                  <div
                    key={plan.label}
                    className="flex items-center justify-between rounded-xl border border-slate-100 bg-white px-3 py-2"
                  >
                    <div>
                      <p className="text-sm font-semibold text-slate-900">
                        {plan.label}
                      </p>
                      <p className="text-[11px] text-slate-500">
                        {plan.count} empresas
                      </p>
                    </div>
                    <Badge color="gray w-fit mx-auto">{plan.count}</Badge>
                  </div>
                ))}
                {!planDistribution.length && (
                  <p className="text-sm text-slate-500">
                    Nenhum plano encontrado.
                  </p>
                )}
              </div>
            </div>
          </div>

          <div className="rounded-2xl border border-slate-200 bg-white shadow-sm">
            <div className="flex items-center justify-between border-b border-slate-100 px-5 py-4">
              <div>
                <p className="text-xs uppercase tracking-[0.3em] text-slate-400">
                  Operação
                </p>
                <p className="text-sm font-semibold text-slate-900">
                  Empresas com atividade recente
                </p>
              </div>
              <Link to="/companies">
                <Button color="light" size="xs">
                  Ver todas
                  <HiOutlineArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </Link>
            </div>
            <div className="overflow-x-auto">
              <Table hoverable className="min-w-[960px]">
                <Table.Head className="bg-slate-50">
                  <Table.HeadCell className="text-[11px] uppercase tracking-[0.3em] text-slate-500">
                    Empresa
                  </Table.HeadCell>
                  <Table.HeadCell className="text-[11px] uppercase tracking-[0.3em] text-slate-500">
                    Status
                  </Table.HeadCell>
                  <Table.HeadCell className="text-[11px] uppercase tracking-[0.3em] text-slate-500">
                    Plano
                  </Table.HeadCell>
                  <Table.HeadCell className="text-right text-[11px] uppercase tracking-[0.3em] text-slate-500">
                    Usuários
                  </Table.HeadCell>
                  <Table.HeadCell className="text-right text-[11px] uppercase tracking-[0.3em] text-slate-500">
                    Funis
                  </Table.HeadCell>
                  <Table.HeadCell className="text-[11px] uppercase tracking-[0.3em] text-slate-500">
                    Última atualização
                  </Table.HeadCell>
                </Table.Head>
                <Table.Body className="divide-y divide-slate-100">
                  {newestCompanies.map((company) => (
                    <Table.Row key={company.id} className="bg-white">
                      <Table.Cell>
                        <p className="text-sm font-semibold text-slate-900">
                          {company.socialReason || company.name}
                        </p>
                        <p className="text-[11px] text-slate-500">
                          {company.email || company.document || "Sem contato"}
                        </p>
                      </Table.Cell>
                      <Table.Cell>
                        <Badge className="w-fit" color={statusColor(company)}>
                          {statusLabel(company)}
                        </Badge>
                      </Table.Cell>
                      <Table.Cell>
                        <p className="text-sm font-semibold text-slate-900">
                          {planLabel(company)}
                        </p>
                        <p className="text-[11px] text-slate-500">
                          Expira em {formatDate(company.planExpirationDate)}
                        </p>
                      </Table.Cell>
                      <Table.Cell className="text-right font-semibold text-slate-900">
                        {company.counts?.users ?? 0}
                      </Table.Cell>
                      <Table.Cell className="text-right font-semibold text-slate-900">
                        {company.counts?.funnels ?? 0}
                      </Table.Cell>
                      <Table.Cell className="text-sm text-slate-500">
                        {formatDate(company.updatedAt)}
                      </Table.Cell>
                    </Table.Row>
                  ))}
                </Table.Body>
              </Table>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
